import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '../lib/utils'

// Minimal full-screen image preview. Tap the backdrop or the X to
// close; the image itself stays interactive (so GIFs animate, the user
// can long-press to save, etc.). Portal-mounted so it always sits above
// any transformed ancestors and ignores parent scroll containers.
//
// Props:
// - open       boolean
// - src        string         — image URL
// - alt        string         — alt text (also the caption)
// - onClose    () => void
export default function ImagePreviewDialog({ open, src, alt, onClose }) {
  // Escape closes the dialog — mirrors the rest of the app's confirm /
  // dropdown patterns.
  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    window.addEventListener('keydown', onKey)
    // Lock body scroll while the dialog is open so phones don't bounce
    // the underlying page when the user taps to dismiss.
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open, onClose])

  if (!open || !src) return null
  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={alt || 'Preview'}
      className="fixed inset-0 z-[80] flex flex-col bg-black/85 backdrop-blur-sm animate-in fade-in"
      onClick={onClose}
      style={{
        // Respect iOS Dynamic Island / notch on top, home indicator at bottom.
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {/* Header row — X sits in a normal-flow row so it never overlaps
          the status bar (the previous absolute-positioned X was landing
          under the clock on iOS). */}
      <div className="flex justify-end p-3 flex-shrink-0">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close preview"
          className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      {/* Image fills whatever space remains below the header, so tall
          portrait shots use the full viewport instead of being shrunk
          to 85vh with a thick dark band above and below. */}
      <div className="flex-1 flex items-center justify-center px-4 min-h-0">
        <img
          src={src}
          alt={alt || ''}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'max-w-full max-h-full rounded-lg shadow-2xl bg-white object-contain',
            'animate-in zoom-in-95'
          )}
        />
      </div>
      {alt && (
        <p
          className="text-center text-sm text-white/90 px-6 pt-3 pb-4 flex-shrink-0"
          dir="auto"
        >
          {alt}
        </p>
      )}
    </div>,
    document.body
  )
}
